import { uuidv7, type Foto } from '@qsms/shared'
import { useRef, useState } from 'react'
import { banco } from '../dados/banco.js'
import { comprimirImagem } from '../dados/imagem.js'
import { useArquivo } from '../dados/useArquivo.js'
import { useValorEditavel } from '../dados/useValorEditavel.js'

/// Fotos soltas da inspeção, com descrição — o "FotosAcoesContainer" do Power Apps.
/// Aceita várias de uma vez, porque em campo o inspetor fotografa a situação toda
/// antes de voltar ao aplicativo.

interface Props {
  inspecaoId: string
  fotos: Foto[]
  somenteLeitura?: boolean
  /// Recebe uma função sobre a lista, e não a lista pronta: quem grava aplica sobre
  /// o que está no banco naquele instante. A lista que esta tela tinha na mão pode
  /// estar velha — a compressão de várias fotos leva segundos, e a pessoa continua
  /// mexendo. `imediato` é para adicionar e remover; a descrição espera a pausa.
  onMudar: (mudar: (fotos: Foto[]) => Foto[], imediato: boolean) => void
}

export function GaleriaFotos({ inspecaoId, fotos, somenteLeitura, onMudar }: Props) {
  const entrada = useRef<HTMLInputElement | null>(null)
  const [ocupado, setOcupado] = useState(false)

  async function adicionar(arquivos: FileList) {
    setOcupado(true)
    try {
      const novas: Foto[] = []
      for (const arquivo of Array.from(arquivos)) {
        const blob = await comprimirImagem(arquivo)
        const arquivoId = uuidv7()
        await banco.fotos.add({
          arquivoId,
          inspecaoId,
          blob,
          mimeType: blob.type,
          criadoEm: new Date().toISOString(),
          enviado: false,
        })
        novas.push({ id: uuidv7(), arquivoId, descricao: null, ordem: 0 })
      }
      onMudar(
        (atuais) => [...atuais, ...novas.map((f, i) => ({ ...f, ordem: atuais.length + i + 1 }))],
        true,
      )
    } finally {
      setOcupado(false)
    }
  }

  // O arquivo da foto sai do aparelho depois que a lista nova estiver gravada — quem
  // grava cuida disso. Apagar antes deixava a inspeção apontando para o nada se o app
  // fechasse no meio.
  function remover(foto: Foto) {
    onMudar(
      (atuais) => atuais.filter((f) => f.id !== foto.id).map((f, i) => ({ ...f, ordem: i + 1 })),
      true,
    )
  }

  return (
    <div className="cartao p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">
          Fotos {fotos.length > 0 && <span className="text-slate-400">({fotos.length})</span>}
        </h3>
        {!somenteLeitura && (
          <button
            type="button"
            onClick={() => entrada.current?.click()}
            className="botao-neutro !min-h-0 !py-1.5 text-sm"
            disabled={ocupado}
          >
            {ocupado ? 'Processando…' : '+ Adicionar'}
          </button>
        )}
      </div>

      <input
        ref={entrada}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) void adicionar(e.target.files)
          e.target.value = ''
        }}
      />

      {fotos.length === 0 ? (
        <p className="py-4 text-center text-sm text-slate-400">
          {somenteLeitura ? 'Nenhuma foto registrada.' : 'Nenhuma foto ainda.'}
        </p>
      ) : (
        <ul className="space-y-3">
          {fotos.map((foto) => (
            <li key={foto.id} className="rounded-lg border border-slate-200 p-2">
              <Miniatura arquivoId={foto.arquivoId} />
              {somenteLeitura ? (
                foto.descricao && <p className="mt-2 text-sm text-slate-600">{foto.descricao}</p>
              ) : (
                <>
                  <DescricaoFoto foto={foto} onMudar={onMudar} />
                  <button
                    type="button"
                    onClick={() => remover(foto)}
                    className="mt-2 text-sm font-medium text-alta"
                  >
                    Remover foto
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/// Descrição com estado próprio: vinda direto do banco, o React desfazia cada letra
/// até a gravação, e cada tecla regravava a inspeção inteira e redesenhava a tela.
function DescricaoFoto({ foto, onMudar }: { foto: Foto; onMudar: Props['onMudar'] }) {
  const [texto, setTexto] = useValorEditavel(foto.descricao ?? '', mesmoTexto)
  return (
    <input
      className="campo mt-2 !py-2 text-sm"
      placeholder="Descrição da foto"
      value={texto}
      onChange={(e) => {
        const novo = e.target.value
        setTexto(novo)
        onMudar((atuais) => atuais.map((f) => (f.id === foto.id ? { ...f, descricao: novo } : f)), false)
      }}
    />
  )
}

const mesmoTexto = (a: string, b: string) => a === b

function Miniatura({ arquivoId }: { arquivoId: string }) {
  const { estado, url } = useArquivo(arquivoId)

  if (estado === 'carregando') return <div className="h-40 w-full animate-pulse rounded bg-slate-100" />
  if (estado === 'ausente') {
    return (
      <div className="grid h-40 w-full place-items-center rounded bg-slate-100 text-sm text-slate-500">
        Foto indisponível sem conexão
      </div>
    )
  }
  return <img src={url} alt="" loading="lazy" className="max-h-64 w-full rounded object-cover" />
}
